import {
  api,
  type Capacity,
  type EventState,
  type Timeline,
  type TimelineEvent,
  type WorkOrder,
} from '../api';
import { errorCard, esc, fmtDate, fmtDecimalHours, fmtInt, html, loading, pill, type State } from '../ui';

const EVENT_CLASS: Record<EventState, string> = {
  OVERDUE: 'ev-exceed',
  DUE_SOON: 'ev-caution',
  PLANNED: 'ev-plan',
  OK: 'ev-ok',
};

const WO_STATE: Record<string, State> = {
  DRAFT: 'NA',
  PLANNED: 'DUE_SOON',
  IN_WORK: 'DUE_SOON',
  AWAITING_PARTS: 'OVERDUE',
  COMPLETED: 'OK',
};

const WO_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  PLANNED: 'Planned',
  IN_WORK: 'In work',
  AWAITING_PARTS: 'Awaiting parts',
  COMPLETED: 'Completed',
};

const ITEM_KIND: Record<string, string> = {
  TASK: 'Programme task',
  DIRECTIVE: 'Airworthiness directive',
  DEFECT: 'Defect rectification',
  ADHOC: 'Ad-hoc',
};

// ── timeline ────────────────────────────────────────────────────────────────

/**
 * A month-gridded lane per aircraft. Positions are percentages of the window,
 * so the bar keeps its meaning at any width — and an event with no duration
 * still gets a minimum width so it stays clickable on a phone.
 */
function timelineCard(t: Timeline): string {
  const from = new Date(t.windowStart).getTime();
  const to = new Date(t.windowEnd).getTime();
  const span = Math.max(to - from, 1);
  const pct = (iso: string) => ((new Date(iso).getTime() - from) / span) * 100;

  const monthLabel = (key: string) =>
    new Date(`${key}-01T00:00:00Z`).toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });

  const lane = (l: Timeline['lanes'][number]) => {
    // Overlapping events are stacked so none is hidden behind another.
    const rows: TimelineEvent[][] = [];
    for (const e of l.events) {
      const left = pct(e.start);
      const right = e.end ? Math.max(pct(e.end), left + 6) : left + 12;
      const row = rows.find((r) =>
        r.every((o) => {
          const ol = pct(o.start);
          const or = o.end ? Math.max(pct(o.end), ol + 6) : ol + 12;
          return right < ol - 1 || left > or + 1;
        }),
      );
      if (row) row.push(e);
      else rows.push([e]);
    }

    const height = Math.max(rows.length, 1) * 26 + 12;

    return html`
      <div class="lane" style="min-height:${height}px">
        <a href="/fleet/${esc(l.aircraftId)}" class="reg">${esc(l.registration)}</a>
      </div>
      <div class="track" style="height:${height}px">
        ${rows
          .map((row, i) =>
            row
              .map((e) => {
                const left = Math.max(pct(e.start), 0);
                const right = e.end ? Math.max(pct(e.end), left + 6) : left + 12;
                const width = Math.min(right - left, 100 - left);
                return html`<div
                  class="ev ${EVENT_CLASS[e.state]}"
                  style="left:${left.toFixed(1)}%;width:${width.toFixed(1)}%;top:${8 + i * 26}px"
                  title="${esc(e.title)} — ${esc(e.detail)}${e.projected ? ' (projected from utilisation)' : ''}"
                >${e.projected ? '≈ ' : ''}${esc(e.title)}</div>`;
              })
              .join(''),
          )
          .join('')}
      </div>
    `;
  };

  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Forecast</h2>
          <p class="tiny dim">
            Hour-driven items are projected onto the calendar from each aircraft's rolling
            utilisation and marked ≈
          </p>
        </div>
        <span class="lbl">${esc(monthLabel(t.months[0]))} – ${esc(monthLabel(t.months[t.months.length - 1]))}</span>
      </div>
      <div class="scrollx">
        <div class="tl" style="min-width:760px;grid-template-columns:130px minmax(0,1fr)">
          <div class="hd lbl">Aircraft</div>
          <div class="months lbl" style="grid-template-columns:repeat(${t.months.length},1fr)">
            ${t.months.map((m) => html`<span>${esc(monthLabel(m))}</span>`).join('')}
          </div>
          ${t.lanes.map(lane).join('')}
        </div>
      </div>
      <div class="card-f">
        Pulling an item forward inside its tolerance band is free. Anything outside the band asks
        for a justification and records it against the task.
      </div>
    </div>
  `;
}

// ── work packs ──────────────────────────────────────────────────────────────

function workOrderCard(w: WorkOrder): string {
  const done = w.items.filter((i) => i.status === 'DONE').length;
  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2><a href="/planning/${esc(w.id)}">${esc(w.reference)}</a> · <a href="/fleet/${esc(w.aircraftId)}">${esc(w.registration)}</a></h2>
          <p class="tiny dim">${esc(w.title)}</p>
        </div>
        <div style="display:flex;gap:8px;align-items:center;margin-left:auto">
          ${pill(WO_STATE[w.status] ?? 'NA', WO_LABEL[w.status] ?? w.status)}
          ${/* Before the work, not after: a quote is only useful while the
                answer can still change what happens. Hidden once the pack is
                finished, where an invoice is the document that applies. */ ''}
          ${w.status === 'COMPLETED' || w.status === 'CANCELLED'
            ? ''
            : html`<button class="btn sm" data-estimate="${esc(w.id)}" data-ref="${esc(w.reference)}">Estimate</button>`}
          <a class="btn sm" href="/planning/${esc(w.id)}">${w.status === 'COMPLETED' ? 'View' : 'Work this pack'}</a>
        </div>
      </div>

      <div class="kv">
        <div>
          <span class="lbl">Planned</span>
          <b>${esc(fmtDate(w.plannedStart))}</b>
          <small>${w.groundTimeDays ? `${w.groundTimeDays} day(s) on the ground` : 'same day'}</small>
        </div>
        <div>
          <span class="lbl">Labour</span>
          <b>${w.estimatedManHours !== null ? `${fmtDecimalHours(w.estimatedManHours)} h` : '—'}</b>
          <small>${w.actualManHours !== null ? `${fmtDecimalHours(w.actualManHours)} h actual` : 'estimated'}</small>
        </div>
        <div>
          <span class="lbl">Items</span>
          <b>${done} / ${w.items.length}</b>
          <small>complete</small>
        </div>
        <div>
          <span class="lbl">Parts</span>
          <b>${w.parts.length}</b>
          <small>${w.partsCost > 0 ? `${w.partsCost.toFixed(2)} cost` : 'none listed'}</small>
        </div>
      </div>

      <div class="rows">
        ${w.items
          .map(
            (i) => html`<div class="row" style="border-left-color:var(--${i.status === 'DONE' ? 'ok' : i.defectSeverity === 'GROUNDING' ? 'exceed' : 'line-2'})">
              <div class="bd">
                <div class="t">${esc(i.taskTitle ?? i.description)}</div>
                <div class="m">
                  ${esc(ITEM_KIND[i.kind] ?? i.kind)}${i.taskCode ? ` · ${esc(i.taskCode)}` : ''}${i.defectReference ? ` · ${esc(i.defectReference)}` : ''}${i.manHours ? ` · est. ${fmtDecimalHours(i.manHours)} h` : ''}
                </div>
              </div>
              ${i.status === 'DONE' ? pill('OK', 'Done') : pill('NA', 'Open')}
            </div>`,
          )
          .join('')}
        ${w.parts
          .map(
            (p) => html`<div class="row">
              <div class="bd">
                <div class="t">${esc(p.description)}</div>
                <div class="m">
                  ${esc(p.partNumber)} · ${p.quantity} ${esc(p.unit)}${p.releaseKind ? ` · ${esc(p.releaseKind.replace(/_/g, ' '))}` : ''}
                </div>
              </div>
              <span class="chip">Part</span>
            </div>`,
          )
          .join('')}
      </div>

      <div class="card-f">
        ${w.performingOrg ? esc(w.performingOrg) : 'organisation not set'}${w.performingApproval ? ` · ${esc(w.performingApproval)}` : ''}
        ${w.notes ? html`<br />${esc(w.notes)}` : ''}
      </div>
    </div>
  `;
}

function capacityCard(c: Capacity): string {
  return html`
    <div class="card">
      <div class="card-h"><h2>Who does the work</h2></div>
      <div class="rows">
        ${c.approvals
          .map(
            (a) => html`<div class="row">
              <div class="bd">
                <div class="t">${esc(a.kind.replace('_', '-'))} — <b class="mono">${esc(a.reference)}</b></div>
                <div class="m">${esc(a.scopeNote ?? '')}</div>
              </div>
            </div>`,
          )
          .join('')}
        ${c.load
          .filter((l) => l.approval)
          .map(
            (l) => html`<div class="row">
              <div class="bd">
                <div class="t">${esc(l.approval ?? '')}</div>
                <div class="m">${l.openWorkOrders} open work pack(s) · ${l.groundDays} ground day(s) committed</div>
              </div>
            </div>`,
          )
          .join('')}
      </div>
      <div class="card-f">
        There is no purchasing module yet, so this reports the approvals on file and the parts
        already listed against planned work rather than inventing a supply chain.
      </div>
    </div>

    ${c.awaitingParts.length > 0
      ? html`<div class="card">
          <div class="card-h">
            <h2>Waiting on parts</h2>
            ${pill('OVERDUE', `${c.awaitingParts.length} held`)}
          </div>
          <div class="rows">
            ${c.awaitingParts
              .map(
                (w) => html`<div class="row" style="border-left-color:var(--exceed)">
                  <div class="bd">
                    <div class="t">${esc(w.reference)} · ${esc(w.registration)} — ${esc(w.title)}</div>
                    <div class="m">
                      planned ${esc(fmtDate(w.plannedStart))} ·
                      ${w.parts.length > 0 ? w.parts.map((p) => `${esc(p.description)} ×${p.quantity}`).join(', ') : 'no parts listed'}
                    </div>
                  </div>
                </div>`,
              )
              .join('')}
          </div>
        </div>`
      : ''}
  `;
}

// ── view ────────────────────────────────────────────────────────────────────

export async function renderPlanning(host: HTMLElement): Promise<void> {
  host.innerHTML = html`
    <div class="phead"><div><h1>Planning &amp; work packs</h1><p>Loading the forecast…</p></div></div>
    ${loading('Projecting the next six months')}
  `;

  let timeline: Timeline;
  let orders: WorkOrder[];
  let capacity: Capacity;
  try {
    [timeline, orders, capacity] = await Promise.all([
      api.planning.timeline(6),
      api.planning.workOrders(),
      api.planning.capacity(),
    ]);
  } catch (e) {
    host.innerHTML = errorCard(e instanceof Error ? e.message : 'Could not load planning.', 'reload');
    host.querySelector('[data-action=reload]')?.addEventListener('click', () => renderPlanning(host));
    return;
  }

  const open = orders.filter((o) => o.status !== 'COMPLETED');
  const completed = orders.filter((o) => o.status === 'COMPLETED');
  const groundDays = open.reduce((n, o) => n + (o.groundTimeDays ?? 0), 0);
  const overdue = timeline.lanes.reduce(
    (n, l) => n + l.events.filter((e) => e.state === 'OVERDUE').length,
    0,
  );

  host.innerHTML = html`
    <div class="phead">
      <div>
        <h1>Planning &amp; work packs</h1>
        <p>
          Six months forward · ${fmtInt(timeline.lanes.length)} aircraft · events projected on
          rolling utilisation
        </p>
      </div>
      <div style="display:flex;gap:8px">
        <button class="btn" data-report="capacity">Capacity view</button>
        <button class="btn pri" data-dialog="work-pack">New work pack</button>
      </div>
    </div>

    <div class="tiles">
      <div class="tile ${overdue ? 't-exceed' : 't-ok'}">
        <span class="lbl">Overdue in the window</span><b>${overdue}</b><span class="sub">must be cleared first</span>
      </div>
      <div class="tile t-caution">
        <span class="lbl">Open work packs</span><b>${open.length}</b><span class="sub">${completed.length} completed</span>
      </div>
      <div class="tile t-acc">
        <span class="lbl">Ground time committed</span><b>${groundDays}</b><span class="sub">days across open packs</span>
      </div>
      <div class="tile ${capacity.awaitingParts.length ? 't-exceed' : 't-ok'}">
        <span class="lbl">Waiting on parts</span><b>${capacity.awaitingParts.length}</b><span class="sub">work packs held</span>
      </div>
    </div>

    ${timelineCard(timeline)}

    <div class="cols3">
      ${open.map(workOrderCard).join('')}
      ${capacityCard(capacity)}
    </div>

    ${completed.length > 0
      ? html`<div class="card">
          <div class="card-h"><h2>Completed</h2><span class="lbl">${fmtInt(completed.length)}</span></div>
          <div class="rows">
            ${completed
              .map(
                (w) => html`<div class="row">
                  <div class="bd">
                    <div class="t">${esc(w.reference)} · ${esc(w.registration)} — ${esc(w.title)}</div>
                    <div class="m">completed ${esc(fmtDate(w.actualEnd))}${w.actualManHours ? ` · ${fmtDecimalHours(w.actualManHours)} man-hours` : ''}</div>
                  </div>
                  ${pill('OK', 'Completed')}
                </div>`,
              )
              .join('')}
          </div>
        </div>`
      : ''}
  `;
}
